import {
  AloftPrice,
  PairedCostAdjustmentType,
  PairedCostResults,
  PropertyTypes,
} from '@aloft/toolkit-shared';
import { Flex, Tooltip, TypographyVariant } from '@aloft/toolkit-sui';

import { Fragment } from 'react';

import { Caret } from '../../components/Caret.tsx';
import { Td } from '../../summary/components/Td.tsx';
import { Tr } from '../../summary/components/Tr.tsx';
import { Typography } from '../../summary/components/Typography.tsx';
import { WebOnly } from '../../summary/components/WebOnly.tsx';
import { AdjustmentTooltip } from './AdjustmentTooltip.tsx';
import { SummaryTableExtraCells } from './SummaryTableExtraCells.tsx';
import { COMPS_PER_TABLE } from './constants.ts';
import { Tkv2PairedCostSummaryInterfaceV2 } from './types.ts';
import { adjustment } from './utils/formatters/adjustment.ts';

const dynamicRowMap: {
  [key in PairedCostAdjustmentType]: 'always' | 'hasFeature' | 'hasPrice';
} = {
  dateOfSale: 'always',
  concessions: 'hasPrice',
  termsOfSale: 'hasPrice',
  functionalUtility: 'always',
  externalObsolescence: 'hasPrice',
  quality: 'always',
  condition: 'always',
  mainDwelling: 'always',
  bathAboveGrade: 'hasPrice',
  bathBelowGrade: 'hasPrice',
  garageCarport: 'always',
  basement: 'always',
  manual: 'hasFeature',
  adu: 'hasFeature',
  attic: 'hasFeature',
  fireplace: 'hasFeature',
  barn: 'hasFeature',
  bonusRoom: 'hasFeature',
  porchPatioDeck: 'hasFeature',
  pool: 'hasFeature',
  shed: 'hasFeature',
  workshop: 'hasFeature',
  manufactured: 'hasFeature',
  belowGradeANSI: 'hasFeature',
  site: 'always',
  siteInfluence: 'hasPrice',
  siteView: 'hasPrice',
  locationView: 'hasPrice',
  foundation: 'hasPrice',
  units: 'hasPrice',
  halfBaths: 'hasPrice',
  fullBaths: 'hasPrice',
  basementHalfBaths: 'hasPrice',
  basementFullBaths: 'hasPrice',
  aduHalfBaths: 'hasPrice',
  aduFullBaths: 'hasPrice',
  basementFinished: 'hasPrice',
  basementTotal: 'hasPrice',
  bedrooms: 'hasPrice'
};

export const SummaryTableRowFromAdjustmentType = ({
  section,
  sequence,
  adjustmentType,
  typeVariant = 'body2',
  isFirstRow,
}: {
  section: Tkv2PairedCostSummaryInterfaceV2;
  sequence: string[];
  adjustmentType: PairedCostAdjustmentType;
  typeVariant?: TypographyVariant;
  isFirstRow?: boolean;
}) => {
  if (!shouldShow(section.results, adjustmentType)) {
    return null;
  }
  const labelTooltip = getLabelTooltip(adjustmentType, section.propertyTypes);

  return (
    <Tr>
      <Td width="16%" isFirstColumn={true} isFirstRow={isFirstRow}>
        <Typography variant={typeVariant}>
          {section.results.adjustmentLabels[adjustmentType]}
        </Typography>
        <WebOnly>
          <Tooltip placement="right" openDelay={500} label={labelTooltip}>
            <Flex cursor="help">
              <Caret />
            </Flex>
          </Tooltip>
        </WebOnly>
      </Td>
      <Td width="12%" isFirstRow={isFirstRow}>
        <Typography variant={typeVariant}>
          {section.results.subject?.features[adjustmentType]?.text ?? ''}
        </Typography>
      </Td>
      {sequence.map((compId) => {
        const breakdown = section.results.comps[compId]?.adjustments?.[adjustmentType];
        const feature = section.results.comps[compId]?.features?.[adjustmentType];
        const { str: adjStr, color } = adjustment(breakdown?.adjustment);

        return (
          <Fragment key={`${compId}-${adjustmentType}`}>
            <Td width="12%" isFirstRow={isFirstRow}>
              <Typography variant={typeVariant}>{feature?.text ?? ''}</Typography>
            </Td>
            <Td width="12%" textAlign="right" isFirstRow={isFirstRow}>
              {adjStr.map((string, idx) => (
                <Typography key={idx} variant="body2" color={color}>
                  {string}
                </Typography>
              ))}
              <WebOnly>{breakdown && <AdjustmentTooltip {...breakdown} />}</WebOnly>
            </Td>
          </Fragment>
        );
      })}
      <SummaryTableExtraCells count={COMPS_PER_TABLE - sequence.length} span={2} />
    </Tr>
  );
};

function shouldShow(results: PairedCostResults, adjustmentType: PairedCostAdjustmentType): boolean {
  const setting = dynamicRowMap[adjustmentType];
  if (setting === 'always') {
    return true;
  }
  return [results.subject, ...Object.values(results.comps)].some((prop) => {
    const adjAmount = prop?.adjustments?.[adjustmentType].adjustment ?? AloftPrice.zero;
    if (!adjAmount.equals(AloftPrice.zero)) {
      return true;
    }
    const text = prop?.features?.[adjustmentType]?.text;
    return setting === 'hasFeature' && text;
  });
}

function getLabelTooltip(
  adjustmentType: PairedCostAdjustmentType,
  propertyTypes: PropertyTypes[],
): string | undefined {
  const propertyTypeSet = new Set(propertyTypes);

  if (adjustmentType === 'mainDwelling') {
    if (propertyTypeSet.size > 1) {
      return `This adjustment includes the value of the GLA, kitchen and bathrooms, and all the various building materials, components and features. 
            We recommend applying this adjustment in the GLA line of the sales comparison grid. 
            
            For manufactured homes this adjustment also includes the value the number of sections, the foundation type, delivery mileage and climate package.
            
            For multi-family homes this adjustment also includes the number of units.`;
    }

    if (propertyTypeSet.has('manufacturedHome')) {
      return `For manufactured homes this adjustment includes the value of the GLA, baths, the number of sections, the foundation type, delivery mileage and climate package. 
            We recommend applying this adjustment in the GLA line of the sales comparison grid`;
    }

    if (propertyTypeSet.has('multiFamily')) {
      return `For multi-family homes this adjustment includes the value of the GBA, baths, stories and number of units.
            We recommend applying this adjustment in the GBA line of the sales comparison grid.`;
    }
  }
  return undefined;
}
